import { useQuery } from '@tanstack/react-query';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths.ts';
import { getLegalEntities } from '@dealstack/frontend/app/services/Person.service.ts';
import { useMemo } from 'react';
import { CurrencyType } from '@dealstack/shared/lib/generated';
import { generateUUID } from '@dealstack/frontend/shared/other/react/generateUUID';
import type {
	LegalPersonOption,
	PendingLegalPerson,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/Loans/components/ManageLoanDrawer/useLegalPersonSelect.types';

interface Props {
	dealId: string;
	selectedPersonId?: string | null;
	pendingPerson?: PendingLegalPerson | null;
}

export const createPendingPerson = (name: string): PendingLegalPerson => ({
	id: generateUUID(),
	name,
});

export const useLegalPersonSelect = ({ dealId, selectedPersonId, pendingPerson }: Props) => {
	const getAllLegalEntitiesRequest = useQuery({
		queryKey: [API_PATHS.PERSON_DEAL_DEALID_LEGAL_PERSONS_GET, { dealId }],
		queryFn: () => getLegalEntities(API_PATHS.PERSON_DEAL_DEALID_LEGAL_PERSONS_GET, { dealId }),
	});

	const pendingPersonId = pendingPerson?.id;
	const pendingPersonName = pendingPerson?.name;

	const personOptions = useMemo(() => {
		const options: LegalPersonOption[] = (getAllLegalEntitiesRequest.data ?? []).map(
			(entity) => ({
				label: entity.displayName,
				value: entity.id,
				entityCurrency: entity.entityCurrency,
			})
		);
		if (
			pendingPersonId &&
			pendingPersonName &&
			!options.some((option) => option.value === pendingPersonId)
		) {
			options.push({
				label: pendingPersonName,
				value: pendingPersonId,
				entityCurrency: CurrencyType.EUR,
				isNew: true,
			});
		}
		return options;
	}, [getAllLegalEntitiesRequest.data, pendingPersonId, pendingPersonName]);

	const selectedPerson = useMemo(() => {
		if (!selectedPersonId) {
			return undefined;
		}
		return personOptions.find((person) => person.value === selectedPersonId);
	}, [selectedPersonId, personOptions]);

	return {
		isLoadingPersons: getAllLegalEntitiesRequest.isLoading,
		personOptions,
		selectedPerson,
		refetchPersons: getAllLegalEntitiesRequest.refetch,
	};
};
